import { ConfirmDialog } from '@/shared/components';
import type { PropertyFileTreeNode } from '@/shared/api';
import { partitionLocked } from './akteBulkRules';
import { BulkMoveDialog } from './BulkMoveDialog';
import { BulkReclassifyDialog } from './BulkReclassifyDialog';
import type { useAkteBulkActions } from './useAkteBulkActions';
import type { useAkteBulkDialogs } from './useAkteBulkDialogs';

interface Props {
  tree: PropertyFileTreeNode;
  dialogs: ReturnType<typeof useAkteBulkDialogs>;
  bulk: ReturnType<typeof useAkteBulkActions>;
  /** Öffnet den (Einzel-)Neuer-Ordner-Dialog für die Kategorie der Auswahl. */
  onCreateSubfolder: (parentFolder: string) => void;
}

/** Rendert die Bulk-Dialoge (Verschieben/Kategorie ändern/Löschen), gespeist aus useAkteBulkDialogs. */
export function AkteBulkActionsHost({ tree, dialogs, bulk, onCreateSubfolder }: Props) {
  const deleteMutable = dialogs.deleteFiles
    ? partitionLocked(dialogs.deleteFiles)
    : { mutable: [], locked: [] };

  return (
    <>
      {dialogs.moveFiles && (
        <BulkMoveDialog
          open
          onOpenChange={(v) => !v && dialogs.closeMove()}
          files={dialogs.moveFiles}
          tree={tree}
          onMove={(target) => void bulk.move(dialogs.moveFiles!, target)}
          onCreateSubfolder={() => {
            const parent = dialogs.moveFiles![0]!.folder;
            dialogs.closeMove();
            onCreateSubfolder(parent);
          }}
        />
      )}

      {dialogs.reclassifyFiles && (
        <BulkReclassifyDialog
          open
          onOpenChange={(v) => !v && dialogs.closeReclassify()}
          files={dialogs.reclassifyFiles}
          suggestedKind={dialogs.reclassifyKind}
          onReclassify={(kind, issuedOn) =>
            bulk.reclassify(dialogs.reclassifyFiles!, kind, issuedOn)
          }
        />
      )}

      {dialogs.deleteFiles && (
        <ConfirmDialog
          open
          onOpenChange={(v) => !v && dialogs.closeDelete()}
          title={`${deleteMutable.mutable.length} Dateien in den Papierkorb verschieben?`}
          description={
            deleteMutable.locked.length > 0
              ? `${deleteMutable.locked.length} gesperrte Belege werden übersprungen. Die übrigen bleiben 30 Tage im Papierkorb.`
              : 'Die Dateien bleiben 30 Tage im Papierkorb und können wiederhergestellt werden.'
          }
          confirmLabel="In den Papierkorb"
          onConfirm={() => {
            void bulk.remove(dialogs.deleteFiles!);
            dialogs.closeDelete();
          }}
        />
      )}
    </>
  );
}
